package nl.volleyplanner.web.controller.thuisprogramma

import kotlinx.coroutines.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.format
import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.entities.Group
import nl.volleyplanner.api.planner.TaskDay
import nl.volleyplanner.api.planner.PlanningProblem
import nl.volleyplanner.api.sports.MatchStatus
import nl.volleyplanner.extensions.now
import nl.volleyplanner.extensions.today
import nl.volleyplanner.web.*
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.BadgeStyle
import nl.volleyplanner.web.base.component.ModalComponent
import nl.volleyplanner.web.base.component.badge
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.simpleComponent
import nl.volleyplanner.web.controller.thuisprogramma.assignee_finder.AssigneeFinder2
import nl.volleyplanner.web.controller.thuisprogramma.assignee_finder.TaskAssigneeStore
import nl.volleyplanner.web.util.BootstrapColor
import org.w3c.dom.HTMLElement

class TaskDayViewComponent(
    private val problem: PlanningProblem,
    private val taskDay: TaskDay,
    private val taskAssigneeStores: Map<String, TaskAssigneeStore>,
    private val thuisprogrammaFilters: ThuisprogrammaFilters,
    private val thuisprogrammaDataManager: ThuisprogrammaDataManager,
    private val teamToFilter: Group?
) : HTMLComponent() {
    private val timeRefreshStateFlow: StateFlow<LocalDateTime>
    private var _refreshJob: Job? = null

    init {
        timeRefreshStateFlow = MutableStateFlow<LocalDateTime>(LocalDateTime.now())

        if (taskDay.date == LocalDate.today()) {
            _refreshJob = CoroutineManager.RENDER_SCOPE.launch {
                while (true) {
                    val currentDateTime = LocalDateTime.now()
                    // Refresh the component every minute for the active/finished matches
                    if (currentDateTime.minute != timeRefreshStateFlow.value.minute) {
                        timeRefreshStateFlow.value = currentDateTime
                    }
                    delay(10_000L)
                }
            }
        }
    }

    override fun onDispose() {
        super.onDispose()
        _refreshJob?.cancel()
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val currentDateTime = timeRefreshStateFlow.value
        //val currentDateTime = LocalDateTime(2026, 1, 24, 11, 30)  // TODO: Remove test date
        var nowIdSet = false

        val matchesToShow = taskDay.matches
            .filter { matchEvent -> thuisprogrammaFilters.showMatch(teamToFilter, matchEvent) && matchEvent.usePlanner }

        return consumer.div (classes = "div-table mt-2 mb-5") {
            id = taskDay.date.format(DATE_FORMAT_ISO)
            div (classes = "d-flex align-items-center gap-3 sticky-top") {
                // Use background from parent container, so sticky-top will look better
                classes += "bg-body"
                h4 {
                    badge(color = BootstrapColor.Primary) {
                        +taskDay.date.format(DATE_TEXT_NO_YEAR_LONG_FORMAT)
                    }
                }
                hr (classes = "flex-grow-1") {}
                span (classes = "text-muted small fw-medium") {
                    +"${matchesToShow.size}"
                    when(matchesToShow.size) {
                        1 -> +" wedstrijd"
                        else -> +" wedstrijden"
                    }
                }
            }

            val matchesByAccommodation = matchesToShow.groupBy { it.match.accommodation }

            if (matchesByAccommodation.size > 1) {
                div (classes = "d-flex gap-1 my-2 small d-print-none") {
                    icon("bi-geo-alt", color = BootstrapColor.Secondary)
                    matchesByAccommodation.forEach { (accommodation, matches) ->
                        div (classes = "px-1 border rounded shadow-sm") {
                            +(accommodation ?: "")
                            span (classes = "bg-body-tertiary text-body-secondary px-1 small fw-light") {
                                +matches.size.toString()
                            }
                        }
                    }
                }
            }

            matchesByAccommodation.forEach { (accommodation, matches) ->
                div (classes = "tp-accommodation-header-container d-flex gap-3 mt-2 mb-1") {
                    div (classes = "tp-accommodation-header px-2 py-1 d-flex align-items-center gap-1 fw-bold") {
                        icon("bi-geo-alt", color = BootstrapColor.Primary)
                        h6 (classes ="m-0") {
                            +(accommodation ?: "")
                        }
                        span (classes = "bg-body-tertiary text-body-secondary px-1 small fw-light") {
                            +matches.size.toString()
                            when(matches.size) {
                                1 -> +" wedstrijd"
                                else -> +" wedstrijden"
                            }
                        }
                    }
                }

                div (classes = "container tp-task-day small") {
                    div (classes = "row header-row d-none d-lg-flex border-bottom bg-body-tertiary text-secondary text-uppercase p-1") {
                        div (classes = "col-lg-1 px-1") { +"Tijd" }
                        div (classes = "col-lg-4 px-1") { +"Teams" }
                        div (classes = "col-lg-1 px-1 text-center") { +"Veld" }
                        div (classes = "col-lg-3 px-1") { +"Scheidsrechter" }
                        div (classes = "col-lg-3 px-1") { +"Teller" }
                    }
                    var count = 0
                    matches
                        .forEach { matchEvent ->
                            val match = matchEvent.match
                            val matchFinished = (match.endDateTime != null && match.endDateTime!! <= currentDateTime) || match.matchStatus == MatchStatus.Completed
                            val matchActive = (match.startDateTime != null && match.startDateTime!! <= currentDateTime && currentDateTime < match.endDateTime!!) && match.matchStatus == MatchStatus.Planned
                            div (classes = "row tp-match position-relative align-items-center") {
                                if (taskDay.date == currentDateTime.date) {
                                    if (match.startDateTime != null && match.startDateTime!! < currentDateTime && !nowIdSet) {
                                        nowIdSet = true
                                        span { id = "now" }
                                    }

                                    classes += when {
                                        matchFinished -> "tp-match-finished"
                                        matchActive -> "tp-match-active"
                                        else -> "tp-match-future"
                                    }
                                }
                                id = "match-${match.id}"

                                // Match time
                                div (classes = "col-2 col-md-2 col-lg-1 p-1") {
                                    if (match.matchStatus == MatchStatus.Cancelled) {
                                        classes += "text-decoration-line-through"
                                    }
                                    span (classes = "fw-bold") { +(match.startDateTime?.format(LOCAL_DT_TIME_FORMAT) ?: "ntb") }
                                }
                                // Teams
                                div (classes = "col-10 col-md-10 col-lg-4 p-1") {
                                    if (match.matchStatus == MatchStatus.Cancelled) {
                                        classes += "text-decoration-line-through"
                                    }
                                    span (classes = "fw-medium") {
                                        // Both teams can be from the 'home' club if they are playing against each other
                                        // TODO: Better way of providing group information with the match so we don't need
                                        //  the check with .contains()
                                        if (teamToFilter?.name?.let { match.teamHome.contains(it) || match.teamGuest.contains(it) } == true) {
                                            classes += "highlighted"
                                        }
                                        +"${match.teamHome} - ${match.teamGuest}"
                                        if (matchFinished && match.startDateTime?.date == currentDateTime.date) {
                                            span(classes = "vp-icon vp-icon-checkered-flag") { span(classes = "visually-hidden") { +"Scheidsrechter:" } }
                                        } else if (matchActive) {
                                            span(classes = "vp-icon vp-icon-volleyball vp-icon-rotate360") { span(classes = "visually-hidden") {+"Scheidsrechter:" } }
                                        }
                                        if (match.matchStatus == MatchStatus.Cancelled && match.matchStatusReason != null) {
                                            br {}
                                            span { strong { +match.matchStatusReason!! } }
                                        }
                                    }
                                }
                                // Field
                                div (classes = "col-2 col-md-2 col-lg-1 p-1 align-self-start align-self-lg-center text-lg-center") {
                                    if (match.matchStatus == MatchStatus.Cancelled) {
                                        classes += "text-decoration-line-through"
                                    }
                                    //                        span (classes = "d-lg-none me-1") { strong { +"Veld:" } }
                                    badge(color = BootstrapColor.Primary) {
                                        span (classes = "d-lg-none") {
                                            +"veld"
                                            unsafe { +"&nbsp;" }
                                        }
                                        +(match.field ?: "-")
                                    }
                                }
                                // Referee(s)
                                div (classes = "col-10 col-md-4 col-lg-3 p-1") {
                                    matchEvent.refereeTasks.forEach { refereeTask ->
                                        div (classes = "row") {
                                            div (classes = "col") {
                                                span (classes = "d-lg-none me-1") { span(classes = "vp-icon vp-icon-whistle") { span(classes = "visually-hidden") {+"Scheidsrechter:" } } }

                                                component(
                                                    TaskAssigneeDisplay(
                                                        problem,
                                                        match,
                                                        refereeTask,
                                                        thuisprogrammaDataManager,
                                                        thuisprogrammaFilters,
                                                        taskAssigneeStores[TaskAssigneeStore.getKey(match, refereeTask)],
                                                        teamToFilter
                                                    )
                                                )
                                            }
                                        }
                                    }
                                }
                                div (classes = "col-2 d-md-none p-1") {}
                                // Scorekeeper(s)
                                div (classes = "col-9 col-md-5 col-lg-3 p-1") {
                                    matchEvent.scoreKeeperTasks.forEach { scoreKeeperTask ->
                                        div (classes = "row") {
                                            div (classes = "col") {
                                                span (classes = "d-lg-none me-1") { span(classes = "vp-icon vp-icon-scoreboard") { span(classes = "visually-hidden") {+"Teller:" } } }
                                                component(
                                                    TaskAssigneeDisplay(
                                                        problem,
                                                        match,
                                                        scoreKeeperTask,
                                                        thuisprogrammaDataManager,
                                                        thuisprogrammaFilters,
                                                        taskAssigneeStores[TaskAssigneeStore.getKey(match, scoreKeeperTask)],
                                                        teamToFilter
                                                    )
                                                )
                                            }
                                        }
                                    }
                                }
                                // (Additional game rules info)
                                // TODO: Determine if we want to keep this or not
//                                div (classes = "col-1") {
//                                    if (matchEvent.gameRuleset != null) {
//                                        button (classes="d-print-none btn position-absolute end-0 bottom-0") {
//                                            onClickFunction = { event ->
//                                                launchAsync {
//                                                    VolleyPlannerApplication.volleyplannerApiClient.getCompetitionRuleSet(
//                                                        matchEvent.gameRuleset!!
//                                                    )
//                                                }.onComplete { youthRulesInstance ->
//                                                    val modal = object : ModalComponent(
//                                                        "Regels voor ${match.teamHome}",
//                                                    ) {
//                                                        override fun FlowContent.renderBody() {
//                                                            simpleComponent(RuleExplanationComponent(youthRulesInstance))
//                                                        }
//                                                    }
//                                                    CoroutineScope(Dispatchers.Main).launch {
//                                                        createStandaloneComponent(modal).show()
//                                                    }
//                                                }
//                                            }
//                                            icon("bi-info-circle", text = "Regels")
//                                        }
//                                    }
//                                }
                            }
                        }
                }
            }
        }
    }

    override suspend fun getState() = timeRefreshStateFlow
}